// NR · Scroll scene: scroll position -> frame of one sprite sheet, drawn on a canvas.
// The sheet loads only after the page has loaded and the scene is within ~1.5 screens,
// never for reduced motion or data saver. Uses getBoundingClientRect, so it works with
// Horizon's desktop .page-wrapper scroller and the mobile document scroller alike.

import { getIntersectionRoot } from '@theme/scroll-container';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const isMobile = window.matchMedia('(max-width: 749px)');

function saveData() {
  const c = navigator.connection;
  return Boolean(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
}

function afterLoad(fn) {
  if (document.readyState === 'complete') fn();
  else window.addEventListener('load', fn, { once: true });
}

function initScene(root) {
  if (root.dataset.nrReady) return;
  root.dataset.nrReady = '1';

  const canvas = root.querySelector('.nr-scrub__canvas');
  const ctx = canvas?.getContext('2d');
  const callouts = [...root.querySelectorAll('[data-at]')];
  const cta = root.querySelector('[data-nr-scrub-cta]');
  const cols = Number(root.dataset.cols) || 6;
  const rows = Number(root.dataset.rows) || 6;
  const frames = Math.min(Number(root.dataset.frames) || cols * rows, cols * rows);

  let sheet = null;
  let fw = 0;
  let fh = 0;
  let shown = -1;
  let current = 0; // eased frame position
  let target = 0;
  let raf = 0;
  let progress = 0;

  const draw = (index) => {
    if (!sheet || !ctx || index === shown) return;
    shown = index;
    const sx = (index % cols) * fw;
    const sy = Math.floor(index / cols) * fh;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(sheet, sx, sy, fw, fh, 0, 0, canvas.width, canvas.height);
  };

  const update = () => {
    raf = 0;
    const r = root.getBoundingClientRect();
    const span = r.height - window.innerHeight;
    progress = span > 0 ? Math.min(1, Math.max(0, -r.top / span)) : 0;
    root.style.setProperty('--p', progress.toFixed(3));
    const pct = progress * 100;
    // Only the latest callout reached is shown; they share one slot
    let active = -1;
    callouts.forEach((c, i) => {
      if (pct >= Number(c.dataset.at)) active = i;
    });
    callouts.forEach((c, i) => c.classList.toggle('is-on', i === active));
    cta?.classList.toggle('is-on', progress > 0.82);
    target = progress * (frames - 1);
    // ease toward the target frame so a 36-frame sheet reads smooth
    current += (target - current) * 0.35;
    if (Math.abs(target - current) < 0.05) current = target;
    draw(Math.round(current));
    if (current !== target) raf = requestAnimationFrame(update);
  };

  const ac = new AbortController();
  let io = null;
  const onScroll = () => {
    if (!root.isConnected) {
      ac.abort();
      io?.disconnect();
      return;
    }
    if (!raf) raf = requestAnimationFrame(update);
  };

  // Text and CTA follow scroll even before (or without) the sheet
  const opts = { passive: true, signal: ac.signal };
  window.addEventListener('scroll', onScroll, opts);
  document.querySelector('.page-wrapper')?.addEventListener('scroll', onScroll, opts);
  window.addEventListener('resize', onScroll, opts);
  update();

  if (reduceMotion.matches || saveData() || !canvas) {
    // Still pose + every line visible (CSS handles reduced motion layout)
    callouts.forEach((c) => c.classList.add('is-on'));
    cta?.classList.add('is-on');
    return;
  }

  const src = isMobile.matches ? root.dataset.srcM : root.dataset.srcD;
  if (!src) return;

  afterLoad(() => {
    io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        const img = new Image();
        img.decoding = 'async';
        img.src = src;
        img
          .decode()
          .then(() => {
            sheet = img;
            fw = Math.floor(img.naturalWidth / cols);
            fh = Math.floor(img.naturalHeight / rows);
            canvas.width = fw;
            canvas.height = fh;
            shown = -1;
            draw(Math.round(current));
            root.classList.add('is-live');
          })
          .catch(() => {
            /* sheet failed: poster stays */
          });
      },
      // Horizon scrolls .page-wrapper on desktop; observe against it so the margin isn't clipped
      { root: getIntersectionRoot(), rootMargin: '150% 0px' }
    );
    io.observe(root);
  });
}

document.querySelectorAll('[data-nr-scrub]').forEach(initScene);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-nr-scrub]').forEach(initScene);
});
